import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentCompany, useCurrentUser, useIdentity } from '@bonusly/who-am-i';

import { HOME_FEED_NAMESPACES } from '../components/i18n/home-feed-namespaces';
import { PageLoading } from '../components/loading';
import { isHomeFeedPath, prefetchHomeModule } from './prefetch-home-module';
import { PUBLIC_PATH_PREFIX } from './public-path';

const getActiveLanguage = (i18n: { language?: string; resolvedLanguage?: string }) => i18n.resolvedLanguage || i18n.language;

export const AppLoading: FC<PropsWithChildren> = ({ children }) => {
	const { isUserIdentified } = useIdentity();
	const company = useCurrentCompany();
	const user = useCurrentUser();
	const { i18n, t } = useTranslation('monarch');
	const locale = user?.locale || company?.locale;
	const i18nRef = useRef(i18n);
	i18nRef.current = i18n;

	const [isLanguageReady, setIsLanguageReady] = useState(false);
	const [isHomeModuleReady, setIsHomeModuleReady] = useState(() => !isHomeFeedPath(window.location.pathname));
	const hasRevealedRef = useRef(false);

	// Warm the homepage chunk while the identity gate is already blocking. Home-feed
	// paths wait for it (non-home paths start ready); a failed fetch falls through
	// to AppContent's Suspense instead of stranding the loading screen.
	useEffect(() => {
		if (window.location.pathname.startsWith(PUBLIC_PATH_PREFIX)) return;

		let cancelled = false;
		prefetchHomeModule()
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) setIsHomeModuleReady(true);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	// Sync locale + homepage namespaces before revealing; a namespace missing after
	// first paint re-suspends AppContent (the double-render flash). Reads i18n via
	// ref so the effect keys off identity/locale only.
	//
	// After the first reveal, a locale change swaps in place: preloading the new
	// language before switching means nothing suspends, so we never drop the tree
	// back to the loading screen (which would tear down router/provider state).
	useEffect(() => {
		if (!isUserIdentified) {
			// Identity loss unmounts the tree, so the next identify (possibly a
			// different user) must pass the full gate again — not the in-place path.
			hasRevealedRef.current = false;
			setIsLanguageReady(false);
			return;
		}

		let cancelled = false;
		const hasRevealed = hasRevealedRef.current;
		if (!hasRevealed) setIsLanguageReady(false);

		const syncTranslations = async () => {
			const client = i18nRef.current;

			try {
				if (locale && getActiveLanguage(client) !== locale) {
					// Preload before switching, on both paths: with resources cached,
					// changeLanguage resolves immediately, so nothing suspends mid-switch
					// (which would blank PageLoading through TranslationProvider's
					// fallback-less boundary) and a stale in-flight sync is abandoned
					// here before it can overwrite a newer locale.
					await client.loadLanguages(locale);
					if (cancelled) return;
					await client.changeLanguage(locale);
				}

				if (!hasRevealed && isHomeFeedPath(window.location.pathname)) {
					await client.loadNamespaces([...HOME_FEED_NAMESPACES]);
				}
			} catch {
				// Failed fetches degrade to fallback-language strings; never block reveal.
			} finally {
				if (!cancelled && !hasRevealed) setIsLanguageReady(true);
			}
		};

		void syncTranslations();

		return () => {
			cancelled = true;
		};
	}, [isUserIdentified, locale]);

	const isReady = isUserIdentified && isLanguageReady && isHomeModuleReady;

	useEffect(() => {
		if (isReady) hasRevealedRef.current = true;
	}, [isReady]);

	// `/public/*` is reachable by anonymous visitors who never become identified;
	// render immediately. Hooks above still run but no-op on the empty identity store.
	if (window.location.pathname.startsWith(PUBLIC_PATH_PREFIX)) {
		return <>{children}</>;
	}

	if (!isReady) {
		return <PageLoading>{t('loading')}</PageLoading>;
	}

	return <>{children}</>;
};
